import { Trans } from "@lingui/macro"
import {
	mdiEmoticonExcited,
	mdiEmoticonNeutral,
	mdiEmoticonSad
} from "@mdi/js"
import Icon from "@mdi/react"
import {
	Box,
	Button,
	Chip,
	Grid,
	Typography
} from "@mui/material"
import { lightTheme } from "commons/themes"
import TemplateSelectionModal from "components/commons/template-selection-modal"
import { NewStep } from "components/workflow/modals"
import {
	AssetMatchesBrandStatus,
	NewStepRequest,
	PriorityLevel,
	WorkflowObjectType,
} from "models"
import React, { ReactElement } from "react"
import { UseRouterParams } from "services/router"

interface MatchProposalItemDataBrandProps extends UseRouterParams {
	assetId: string
	matchId: string
	logo: string
	brandName: string
	priority: PriorityLevel
	compatibility?: number
	status: AssetMatchesBrandStatus
	notes: string
	createWorkflow: (templateId: string) => void
	createReminder: (request: NewStepRequest) => void
}

interface MatchProposalItemDataBrandState {
	openActions: boolean
	openWorkflow: boolean
	openReminder: boolean
}

export class MatchProposalItemDataBrand extends React.Component<MatchProposalItemDataBrandProps, MatchProposalItemDataBrandState> {
	private readonly actionButtonRef: React.RefObject<any> = React.createRef()

	constructor(props: MatchProposalItemDataBrandProps) {
		super(props)
		this.state = {
			openActions: false,
			openWorkflow: false,
			openReminder: false
		}
	}

	private getPriorityChipData = (priorityLevel: PriorityLevel): ReactElement => {
		switch (priorityLevel) {
			case PriorityLevel.URGENTE:
				return <Chip label={PriorityLevel.URGENTE} color="error" />
			case PriorityLevel.ALTA:
				return <Chip label={PriorityLevel.ALTA} color="error" />
			case PriorityLevel.NORMALE:
				return <Chip label={PriorityLevel.NORMALE} sx={{ backgroundColor: "#ef832e" }} />
			case PriorityLevel.BASSA:
				return <Chip label={PriorityLevel.BASSA} sx={{ backgroundColor: "#fef022" }} />
		}
	}
	private getStatusChipData = (status: AssetMatchesBrandStatus): any => {
		switch (status) {
			case AssetMatchesBrandStatus.CREATED:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.CREATED"}>
								Creato
							</Trans>
						}
						sx={{ backgroundColor: "#adb5bd", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.APPROVED:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.APPROVED"}>
								Approvato
							</Trans>
						}
						sx={{ backgroundColor: "success.light", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.INSPECTION:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.INSPECTION"}>
								Sopralluogo
							</Trans>
						}
						sx={{ backgroundColor: "primary.main", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.INTERESTED:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.INTERESTED"}>
								Interessato
							</Trans>
						}
						sx={{ backgroundColor: "secondary.main", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.SENT:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.SENT"}>
								Inviato
							</Trans>
						}
						sx={{ backgroundColor: "#ef832e", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.REJECTED:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.REJECTED"}>
								Rifiutato
							</Trans>
						}
						sx={{ backgroundColor: "#000000", textTransform: "uppercase", color: "white" }}
					/>
				)
			case AssetMatchesBrandStatus.CONTRACT_DONE:
				return (
					<Chip
						label={
							<Trans id={"enums.asset-matches-brand-status.CONTRACT_DONE"}>
								Contrattualizzata
							</Trans>
						}
						sx={{ backgroundColor: "success.light", textTransform: "uppercase", color: "white" }}
					/>
				)
		}
	}

	private ellipsis = (str: string, num: number = str.length, ellipsisStr = "...") =>
		str.length >= num
			? str.slice(0, num >= ellipsisStr.length ? num - ellipsisStr.length : num) +
			ellipsisStr
			: str;

	private getCompatibility = (compatibility: number): ReactElement => {

		let emoticon: ReactElement = (
			<Box sx={{ display: "flex", gap: 2 }}>
				<Icon path={mdiEmoticonExcited} size={1} color={lightTheme.palette.info.main} />
				<Icon path={mdiEmoticonNeutral} size={1} style={{ color: "#adb5bd" }} />
				<Icon path={mdiEmoticonSad} size={1} style={{ color: "#adb5bd" }} />
			</Box>
		)

		if (compatibility <= 66) {
			emoticon = (
				<Box sx={{ display: "flex", gap: 2 }}>
					<Icon path={mdiEmoticonExcited} size={1} style={{ color: "#adb5bd" }} />
					<Icon path={mdiEmoticonNeutral} size={1} color={lightTheme.palette.info.main} />
					<Icon path={mdiEmoticonSad} size={1} style={{ color: "#adb5bd" }} />
				</Box>
			)
		}

		if (compatibility <= 33) {
			emoticon = (
				<Box sx={{ display: "flex", gap: 2 }}>
					<Icon path={mdiEmoticonExcited} size={1} style={{ color: "#adb5bd" }} />
					<Icon path={mdiEmoticonNeutral} size={1} style={{ color: "#adb5bd" }} />
					<Icon path={mdiEmoticonSad} size={1} color={lightTheme.palette.info.main} />
				</Box>
			)
		}

		return emoticon
	}

	render() {
		return (
			<>
				<Grid item md={1}>
					<img src={this.props.logo} alt="logo" width={50} style={{ borderRadius: 30 }} />
				</Grid>
				<Grid item md={2}>
					<Typography fontWeight="bold" fontSize="0.8rem">
						{this.props.brandName}
					</Typography>
				</Grid>
				<Grid item md={2}>
					{this.getCompatibility(this.props.compatibility || 0)}
				</Grid>
				<Grid item md={4}>
					<Typography fontWeight="bold" fontSize="0.8rem">
						{this.props.notes && this.props.notes !== "" ? this.ellipsis(this.props.notes, 30) : "-"}
					</Typography>
				</Grid>
				<Grid md={2.5}>
					<Button
						variant={"contained"}
						color={"info"}
						sx={{
							px: 1,
							fontSize: "0.8rem"
						}}
						onClick={() => this.props.navigate(`/match/${this.props.matchId}`)}
					>
						<Trans id={"dms-redirect-match-page"}>Vedi proposta</Trans>
					</Button>
				</Grid>

				{this.state.openWorkflow && <TemplateSelectionModal
					templateType={WorkflowObjectType.ASSET}
					onClose={() => this.setState(() => ({ openWorkflow: false }))}
					submit={(templateId: string) => {
						this.setState(() => ({ openWorkflow: false }))
						this.props.createWorkflow(templateId)
					}}
				/>}
				{this.state.openReminder && <NewStep
					openDialog
					onClose={() => this.setState(() => ({ openReminder: false }))}
					submit={(request: NewStepRequest) => {
						this.setState(() => ({ openReminder: false }))
						this.props.createReminder(request)
					}}
					action
					create
				/>}
			</>
		)
	}
}
